import { startOfDay, endOfDay } from "date-fns";

/**
 * Resolve a driver id from a leg or driver-shaped object.
 *
 * @param {object} leg
 * @returns {string|null}
 */
export function getLegDriverId(leg) {
  const driver = leg?.driver;
  if (!driver) return null;
  if (typeof driver === "string") return driver;
  return driver._id || driver.id || null;
}

/**
 * Whether a dispatch leg can be added to a new Manifest V2 trip draft.
 *
 * @param {object} leg
 * @returns {boolean}
 */
export function isLegEligibleForManifestV2Trip(leg) {
  if (!leg?._id) return false;
  if (leg.manifestV2Id) return false;
  return true;
}

/**
 * Build an operational date range that covers all leg appointment dates.
 *
 * @param {object[]} legs
 * @returns {{ startDate: Date, endDate: Date }}
 */
export function buildOperationalDateRangeFromLegs(legs = []) {
  const dates = legs
    .map((leg) => leg?.appointmentDate)
    .filter(Boolean)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()));

  if (!dates.length) {
    const today = new Date();
    return { startDate: startOfDay(today), endDate: endOfDay(today) };
  }

  const minTime = Math.min(...dates.map((date) => date.getTime()));
  const maxTime = Math.max(...dates.map((date) => date.getTime()));

  return {
    startDate: startOfDay(new Date(minTime)),
    endDate: endOfDay(new Date(maxTime)),
  };
}

/**
 * Build navigation state for Manifest V2 create from dispatch leg(s).
 *
 * @param {object|object[]} legs
 * @param {object} [options]
 * @param {string} [options.driverId] - override driver (e.g. assign modal selection)
 * @returns {object|null}
 */
export function buildManifestV2TripDraft(legs, { driverId } = {}) {
  const normalized = (Array.isArray(legs) ? legs : [legs]).filter(Boolean);
  const eligible = normalized.filter(isLegEligibleForManifestV2Trip);

  if (!eligible.length) {
    return null;
  }

  const legIds = eligible.map((leg) => String(leg._id));
  const resolvedDriverId =
    driverId ||
    eligible.map(getLegDriverId).find(Boolean) ||
    null;
  const { startDate, endDate } = buildOperationalDateRangeFromLegs(eligible);

  return {
    source: "dispatch",
    driverId: resolvedDriverId,
    legIds,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    manualSelection: true,
  };
}
